// ============================================================================
// CANCUN100 — HorariosTab.js  (Pestaña HORARIOS: calendario mensual de turnos)
// ----------------------------------------------------------------------------
// Agente: SOLO LECTURA (ve el calendario completo del equipo). Admin: editable
// (popup del día, plantillas semanales, "Generar mes", CRUD de IPs). 100%
// presentación + CRUD sobre horario_turnos / horario_plantilla / oficina_ips.
// NO toca comisiones, pagos, vistas SQL ni PDFs. Tema claro/oscuro vía tokens
// Aurelia (var(--au-*)) + objeto C (ya tema-aware). No convierte C a CSS.
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { RADIO, RADIO_TARJETA, RADIO_INSIGNIA } from './tema';
import { Ic } from './iconos'; // APP 2/24

// ── Constantes de dominio ──
const TURNOS = {
  manana: { label: 'Mañana', horario: '9:00–15:00', icon: '🌅' },
  tarde:  { label: 'Tarde',  horario: '15:00–21:00', icon: '🌆' },
  dia:    { label: 'Día',    horario: '9:00–21:00', icon: '☀️' },
};
const MOD = {
  oficina:  { label: 'Oficina',  icon: '🏢', c: (C) => C.blue },
  casa:     { label: 'Casa',     icon: '🏠', c: (C) => C.green },
  guardia:  { label: 'Guardia',  icon: '🛡️', c: (C) => C.purple },
  descanso: { label: 'Descanso', icon: '😴', c: (C) => C.muted },
  extra:    { label: 'Extra',    icon: '➕', c: (C) => C.naranja },
  inactivo: { label: 'Inactivo', icon: '⛔', c: (C) => C.muted }, // APP 2/27 (RA-6): día apagado para el agente
};
const DIAS_COR = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DIAS_LAR = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// ── Helpers de fecha (locales; los vendedores operan en Cancún) ──
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// dia_semana 1=lunes … 7=domingo (a partir de getDay(): 0=dom … 6=sáb).
const diaSemana = (d) => ((d.getDay() + 6) % 7) + 1;
const esFinde = (ds) => ds === 6 || ds === 7; // sábado o domingo
const primerNombre = (n) => String(n || '').trim().split(/\s+/)[0] || n;

// APP 2/19: el agente ve sus servicios incrustados en cada día (eventos de Agenda.eventosDe) y los balances BR/BT por cobrar; tocar abre el folio.
const n0 = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const $0 = (v) => '$' + n0(v).toLocaleString('es-MX', { maximumFractionDigits: 0 });
export default function HorariosTab({ C, agentes = [], isAgent = false, loggedAgent = null, eventos = [], balances = [], onAbrirFolio = null, movil = false }) {
  const [anchor, setAnchor] = useState(() => new Date());
  const [turnos, setTurnos] = useState([]);       // horario_turnos del mes visible
  const [plantillas, setPlantillas] = useState([]); // horario_plantilla (todas)
  const [notas, setNotas] = useState([]);         // horario_notas_dia del mes visible
  const [ips, setIps] = useState([]);             // oficina_ips (admin)
  const [loading, setLoading] = useState(true);
  const [dayPopup, setDayPopup] = useState(null); // Date del día seleccionado
  const [adminSec, setAdminSec] = useState('calendario'); // 'calendario' | 'plantillas' | 'ips'
  const [busy, setBusy] = useState(false);

  // Estilos base (tema-aware vía CSS vars de Aurelia).
  const card = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, backdropFilter: 'blur(var(--au-glass-blur))', WebkitBackdropFilter: 'blur(var(--au-glass-blur))', boxShadow: 'var(--au-shadow-card)', padding: 16, marginBottom: 14 };

  // Agentes asignables (listados de asignación / plantillas / sidebar): excluir es_fantasma
  // e inactivos (activo === false). Los turnos históricos de un inactivo se siguen mostrando
  // en la cuadrícula/popup (se pintan desde horario_turnos, no desde esta lista).
  const agentesAsignables = agentes.filter((a) => !a.es_fantasma && a.activo !== false);
  const agName = (id) => { const a = agentes.find((x) => String(x.id) === String(id)); return a ? a.nombre : `Agente #${id}`; };

  // ── Carga de datos del mes visible ──
  const monthBounds = useCallback((base) => {
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    return { first, last };
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { first, last } = monthBounds(anchor);
      const reqs = [
        supabase.from('horario_turnos').select('*').gte('fecha', ymd(first)).lte('fecha', ymd(last)),
        supabase.from('horario_plantilla').select('*'),
        supabase.from('horario_notas_dia').select('*').gte('fecha', ymd(first)).lte('fecha', ymd(last)),
      ];
      if (!isAgent) reqs.push(supabase.from('oficina_ips').select('*').order('etiqueta'));
      const res = await Promise.all(reqs);
      setTurnos(res[0].data || []);
      setPlantillas(res[1].data || []);
      setNotas(res[2].data || []);
      if (!isAgent && res[3]) setIps(res[3].data || []);
    } catch (e) { console.error('Horarios cargar:', e); }
    setLoading(false);
  }, [anchor, isAgent, monthBounds]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Cuadrícula del mes (semanas lunes→domingo) ──
  const { first, last } = monthBounds(anchor);
  const leading = diaSemana(first) - 1; // celdas vacías antes del día 1
  const totalDias = last.getDate();
  const celdas = [];
  for (let i = 0; i < leading; i++) celdas.push(null);
  for (let d = 1; d <= totalDias; d++) celdas.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
  while (celdas.length % 7 !== 0) celdas.push(null);

  const hoy = new Date();
  const esHoy = (d) => d && d.getFullYear() === hoy.getFullYear() && d.getMonth() === hoy.getMonth() && d.getDate() === hoy.getDate();
  const turnosDe = (d) => turnos.filter((t) => t.fecha === ymd(d));
  const plantillaDe = (agId, ds) => plantillas.find((p) => String(p.agente_id) === String(agId) && Number(p.dia_semana) === ds);
  const notaDe = (d) => notas.find((n) => n.fecha === ymd(d)) || null;
  // APP 2/19: servicios del agente y balances BR/BT por cobrar en su día (solo isAgent).
  const eventosDeDia = (d) => (isAgent ? (eventos || []).filter((e) => e.fecha === ymd(d)) : []);
  const balancesDeDia = (d) => (isAgent ? (balances || []).filter((b) => n0(b.pendiente) > 0 && ['BR', 'BT'].includes(String(b.pago_balance || '').toUpperCase()) && String(b.fecha_esperada || '').slice(0, 10) === ymd(d)) : []);
  const abrirFolio = (ev, reservaId, folio) => { ev.stopPropagation(); ev.preventDefault(); if (onAbrirFolio) onAbrirFolio(reservaId, folio); };

  // Guardar/borrar la nota del día (admin). Vacío en ambos campos → borra la fila.
  const guardarNota = async (fecha, emoji, nota) => {
    setBusy(true);
    try {
      const e = (emoji || '').trim(), n = (nota || '').trim();
      if (!e && !n) {
        const { error } = await supabase.from('horario_notas_dia').delete().eq('fecha', fecha);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('horario_notas_dia').upsert([{ fecha, emoji: e || null, nota: n || null }], { onConflict: 'fecha' });
        if (error) throw error;
      }
      await cargar();
    } catch (err) { alert('No se pudo guardar la nota: ' + (err.message || err)); }
    setBusy(false);
  };

  const navMes = (dir) => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  // APP 2/21: calendario del agente en mes o semana, y «Próximos 7 días» (servicios de sus folios, llegadas y balances BR/BT).
  const [vistaCal, setVistaCal] = useState(movil && isAgent ? 'dia' : 'mes'); // APP 2/24: en móvil arranca por día
  const [diaSel, setDiaSel] = useState(() => new Date());
  const navSemana = (dir) => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + dir * 7));
  const lunesDe = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12); x.setDate(x.getDate() - (diaSemana(x) - 1)); return x; };
  const diasSemana = Array.from({ length: 7 }, (_, i) => { const x = lunesDe(anchor); x.setDate(x.getDate() + i); return x; });
  const fechaDia = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short' }).replace('.', '');
  const proximos = (() => { if (!isAgent) return []; const h = ymd(new Date()); const fin = ymd(new Date(Date.now() + 6 * 864e5));
    const ev = (eventos || []).filter((e) => e.fecha >= h && e.fecha <= fin);
    const bal = (balances || []).filter((b) => n0(b.pendiente) > 0 && ['BR', 'BT'].includes(String(b.pago_balance || '').toUpperCase()) && String(b.fecha_esperada || '').slice(0, 10) >= h && String(b.fecha_esperada || '').slice(0, 10) <= fin).map((b) => ({ fecha: String(b.fecha_esperada).slice(0, 10), balance: b }));
    return [...ev, ...bal].sort((a, b) => a.fecha.localeCompare(b.fecha)); })();

  // ════════════════════════════════════════════════════════════════════
  // Popup del día — lectura (agente) y edición (admin)
  // ════════════════════════════════════════════════════════════════════
  const [editState, setEditState] = useState({}); // { [agente_id]: {turno, modalidad, notas} }

  const abrirDia = (d) => {
    if (!isAgent) {
      const tds = turnosDe(d);
      const st = {};
      tds.forEach((t) => { st[t.agente_id] = { turno: t.turno, modalidad: t.modalidad, notas: t.notas || '' }; });
      setEditState(st);
    }
    setDayPopup(d);
  };

  const guardarDia = async () => {
    if (!dayPopup) return;
    setBusy(true);
    try {
      const fecha = ymd(dayPopup);
      const ids = agentesAsignables.map((a) => a.id);
      // Reescribe el día para los agentes listados: borra sus filas y reinserta las asignadas.
      await supabase.from('horario_turnos').delete().eq('fecha', fecha).in('agente_id', ids);
      const rows = Object.entries(editState)
        .filter(([, v]) => v && v.turno && v.modalidad)
        .map(([agente_id, v]) => ({
          fecha, agente_id: Number(agente_id), turno: v.turno, modalidad: v.modalidad,
          notas: v.notas || null, creado_por: loggedAgent?.nombre || null, // firma: nombre del admin
        }));
      if (rows.length) {
        const { error } = await supabase.from('horario_turnos').upsert(rows, { onConflict: 'fecha,agente_id,turno' });
        if (error) throw error;
      }
      setDayPopup(null);
      await cargar();
    } catch (e) { alert('No se pudo guardar el día: ' + (e.message || e)); }
    setBusy(false);
  };

  // ── Generar mes desde plantillas (sin pisar filas existentes) ──
  const generarMes = async () => {
    if (!window.confirm(`¿Generar el rol de ${MESES[anchor.getMonth()]} ${anchor.getFullYear()} desde las plantillas? No se sobrescriben ediciones ya hechas.`)) return;
    setBusy(true);
    try {
      const rows = [];
      for (let d = 1; d <= totalDias; d++) {
        const fecha = new Date(anchor.getFullYear(), anchor.getMonth(), d);
        const ds = diaSemana(fecha);
        agentesAsignables.forEach((a) => {
          const pl = plantillaDe(a.id, ds);
          if (pl) rows.push({ fecha: ymd(fecha), agente_id: a.id, turno: pl.turno, modalidad: pl.modalidad, creado_por: loggedAgent?.nombre || null });
        });
      }
      if (rows.length === 0) { alert('No hay plantillas capturadas todavía.'); setBusy(false); return; }
      // Insert con onConflict ignore: las ediciones manuales del admin sobreviven.
      const { error } = await supabase.from('horario_turnos').upsert(rows, { onConflict: 'fecha,agente_id,turno', ignoreDuplicates: true });
      if (error) throw error;
      await cargar();
      alert(`Rol generado: ${rows.length} asignaciones base (las existentes se respetaron).`);
    } catch (e) { alert('No se pudo generar el mes: ' + (e.message || e)); }
    setBusy(false);
  };

  // Chip compacto de agente+modalidad
  const AgenteChip = ({ t, small }) => {
    const m = MOD[t.modalidad] || MOD.oficina;
    const col = m.c(C);
    const mio = loggedAgent && String(t.agente_id) === String(loggedAgent.id);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: `${col}18`, color: col, border: `1px solid ${col}${mio ? '' : '40'}`, boxShadow: mio ? `0 0 0 1.5px ${col}55` : undefined, borderRadius: RADIO, padding: small ? '1px 5px' : '3px 8px', fontSize: small ? 9 : 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {m.icon} {primerNombre(agName(t.agente_id))}
      </span>
    );
  };

  return (
    <div>
      {/* ── Cabecera: navegación de mes + (admin) subsecciones ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--au-text-primary)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Ic n="CalendarDays" size={22} />Horarios{isAgent ? '' : ' — Admin'}
        </div>
        {(isAgent || adminSec === 'calendario') && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginLeft: isAgent ? 'auto' : 0 }}>
            <button onClick={() => (isAgent && vistaCal !== 'mes' ? navSemana(-1) : navMes(-1))} className="au-btn au-btn--secondary" style={{ padding: '6px 12px' }} aria-label="Anterior"><Ic n="ChevronLeft" /></button>
            <div className="au-num" style={{ fontSize: 14, fontWeight: 700, color: 'var(--au-text-primary)', minWidth: 150, textAlign: 'center', textTransform: 'capitalize' }}>{isAgent && vistaCal !== 'mes' ? `${diasSemana[0].getDate()}–${diasSemana[6].getDate()} ${MESES[diasSemana[6].getMonth()]}` : `${MESES[anchor.getMonth()]} ${anchor.getFullYear()}`}</div>
            <button onClick={() => (isAgent && vistaCal !== 'mes' ? navSemana(1) : navMes(1))} className="au-btn au-btn--secondary" style={{ padding: '6px 12px' }} aria-label="Siguiente"><Ic n="ChevronRight" /></button>
            <button onClick={() => setAnchor(new Date())} className="au-btn au-btn--ghost" style={{ height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12 }}>Hoy</button>
            {isAgent && <div className="au-toggle">{movil && <button aria-pressed={vistaCal === 'dia'} onClick={() => setVistaCal('dia')}>Día</button>}<button aria-pressed={vistaCal === 'mes'} onClick={() => setVistaCal('mes')}>Mes</button>{!movil && <button aria-pressed={vistaCal === 'semana'} onClick={() => setVistaCal('semana')}>Semana</button>}</div>}
          </div>
        )}
        {!isAgent && (
          <div className="au-toggle" style={{ marginLeft: 'auto' }}>
            <button aria-pressed={adminSec === 'calendario'} onClick={() => setAdminSec('calendario')}>Calendario</button>
            <button aria-pressed={adminSec === 'plantillas'} onClick={() => setAdminSec('plantillas')}>Plantillas</button>
            <button aria-pressed={adminSec === 'ips'} onClick={() => setAdminSec('ips')}>IPs oficina</button>
          </div>
        )}
      </div>

      {/* Leyenda de modalidades */}
      {(isAgent || adminSec === 'calendario') && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10, fontSize: 12, color: C.muted }}>
          {Object.entries(MOD).map(([k, m]) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{m.icon} {m.label}</span>
          ))}
          <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>Mañana {TURNOS.manana.horario} · Tarde {TURNOS.tarde.horario}</span>
        </div>
      )}

      {loading ? (
        <div style={{ ...card, textAlign: 'center', padding: 40, color: C.muted }}>Cargando calendario…</div>
      ) : (!isAgent && adminSec === 'plantillas') ? (
        <PlantillasEditor C={C} card={card} agentes={agentesAsignables} plantillas={plantillas} recargar={cargar} loggedAgent={loggedAgent} />
      ) : (!isAgent && adminSec === 'ips') ? (
        <IpsEditor C={C} card={card} ips={ips} recargar={cargar} />
      ) : (
        <>
          {/* Barra de acciones admin del calendario */}
          {!isAgent && (
            <div style={{ marginBottom: 10 }}>
              <button onClick={generarMes} disabled={busy} className="au-btn au-btn--primary" style={{ fontSize: 12 }}>
                {busy ? 'Procesando…' : 'Generar mes desde plantillas'}
              </button>
              <span style={{ marginLeft: 10, fontSize: 12, color: C.muted }}>Crea el rol base sin pisar lo que ya editaste. Clic en un día para ajustar.</span>
            </div>
          )}

          {(!isAgent || vistaCal === 'mes') && (<>
          {/* ── Cuadrícula del calendario ── */}
          <div style={{ ...card, padding: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6, marginBottom: 6 }}>
              {DIAS_COR.map((d, i) => (
                <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: i >= 5 ? C.naranja : 'var(--au-text-faint)' }}>{d}</div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
              {celdas.map((d, idx) => {
                if (!d) return <div key={idx} style={{ minHeight: 92, borderRadius: RADIO_TARJETA, background: 'var(--au-glass-fill)', opacity: 0.35 }} />;
                const ds = diaSemana(d);
                const finde = esFinde(ds);
                const tds = turnosDe(d);
                const nota = notaDe(d);
                // La guardia se pinta con su propio badge (AM/PM); no debe repetirse como chip de turno.
                const manana = tds.filter((t) => t.turno === 'manana' && t.modalidad !== 'descanso' && t.modalidad !== 'guardia');
                const tarde = tds.filter((t) => t.turno === 'tarde' && t.modalidad !== 'descanso' && t.modalidad !== 'guardia');
                const guardias = tds.filter((t) => t.modalidad === 'guardia');
                const hoyCell = esHoy(d);
                return (
                  <button key={idx} onClick={() => abrirDia(d)}
                    style={{ minHeight: 92, minWidth: 0, overflow: 'hidden', textAlign: 'left', cursor: 'pointer', borderRadius: RADIO, padding: 7, display: 'flex', flexDirection: 'column', gap: 4,
                      background: hoyCell ? `${C.rosa}12` : (finde ? `${C.naranja}08` : 'var(--au-glass-fill)'),
                      border: hoyCell ? `1px solid ${C.rosa}` : `1px solid var(--au-glass-border)` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: hoyCell ? C.rosa : 'var(--au-text-primary)' }}>{d.getDate()}</span>
                      {tds.length > 0 && <span style={{ fontSize: 12, color: C.muted }}>{tds.filter((t) => t.modalidad !== 'descanso').length} en turno</span>}
                    </div>
                    {/* Nota del día (destacada) */}
                    {nota && (
                      <div title={nota.nota || ''} style={{ display: 'flex', alignItems: 'center', gap: 3, background: `${C.rosa}18`, color: C.rosa, border: `1px solid ${C.rosa}55`, borderRadius: RADIO_INSIGNIA, padding: '2px 6px', fontSize:12, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {nota.emoji || '📣'} {nota.nota}
                      </div>
                    )}
                    {/* La GUARDIA salta directo a la cuadrícula con su turno (AM/PM), sin duplicar */}
                    {guardias.map((g) => {
                      const tl = g.turno === 'manana' ? 'AM' : g.turno === 'tarde' ? 'PM' : 'Día';
                      return (
                        <span key={g.id || g.agente_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: `${C.purple}20`, color: C.purple, border: `1px solid ${C.purple}55`, borderRadius: RADIO_INSIGNIA, padding: '2px 6px', fontSize: 12, fontWeight: 800 }}>
                          {<Ic n="Shield" chico />} {tl} · {primerNombre(agName(g.agente_id))}
                        </span>
                      );
                    })}
                    {/* Chips compactos por turno */}
                    {manana.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{manana.slice(0, 3).map((t) => <AgenteChip key={t.id || t.agente_id} t={t} small />)}{manana.length > 3 && <span style={{ fontSize: 12, color: C.muted }}>+{manana.length - 3}</span>}</div>}
                    {tarde.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{tarde.slice(0, 3).map((t) => <AgenteChip key={t.id || t.agente_id} t={t} small />)}{tarde.length > 3 && <span style={{ fontSize: 12, color: C.muted }}>+{tarde.length - 3}</span>}</div>}
                    {/* APP 2/19: servicios del agente ese día (folio · cliente · servicio · pickup), chip Llegada en el primero; BR/BT por cobrar en ámbar */}
                    {eventosDeDia(d).slice(0, 3).map((e, i) => (
                      <span key={'e' + i} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, e.reserva_id, e.folio)} onKeyDown={(ev) => { if (ev.key === 'Enter') abrirFolio(ev, e.reserva_id, e.folio); }} title={`${e.folio} · ${e.cliente} · ${e.nombre}${e.pickup ? ' · pickup ' + e.pickup : ''}`}
                        style={{ display: 'flex', flexDirection: 'column', gap: 1, background: `${C.rosa}12`, border: `1px solid ${C.rosa}40`, borderRadius: RADIO_INSIGNIA, padding: '3px 6px', fontSize: 12, lineHeight: 1.25, color: 'var(--au-text-primary)', cursor: 'pointer', minWidth: 0, textAlign: 'left' }}>
                        <span style={{ display: 'flex', gap: 4, alignItems: 'center', minWidth: 0 }}>{e.esPrimero && <span style={{ fontWeight: 800, color: 'var(--au-success-fg)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Llegada</span>}<strong style={{ color: C.rosa }}>{e.folio}</strong><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.cliente}</span></span>
                        <span style={{ color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nombre}{e.pickup ? ` · ${e.pickup}` : ''}</span>
                      </span>
                    ))}
                    {eventosDeDia(d).length > 3 && <span style={{ fontSize: 12, color: C.muted }}>+{eventosDeDia(d).length - 3} servicios</span>}
                    {balancesDeDia(d).map((b) => (
                      <span key={'b' + b.reserva_id} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, b.reserva_id, b.folio)} onKeyDown={(ev) => { if (ev.key === 'Enter') abrirFolio(ev, b.reserva_id, b.folio); }} title={`Cobrar balance ${b.pago_balance} de ${b.folio}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: `${C.yellow}1f`, color: C.yellow, border: `1px solid ${C.yellow}`, borderRadius: RADIO_INSIGNIA, padding: '2px 6px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                        Cobras {String(b.pago_balance).toUpperCase()} · {b.folio} · {$0(b.pendiente)}
                      </span>
                    ))}
                  </button>
                );
              })}
            </div>
          </div>
          </>)}
          {isAgent && vistaCal === 'dia' && (() => { // APP 2/24: móvil — franja de la semana (punto en los días con algo) + lista del día elegido
            const evsSel = eventosDeDia(diaSel); const balsSel = balancesDeDia(diaSel);
            return (
            <div style={{ ...card, padding: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
                {diasSemana.map((d) => { const n = eventosDeDia(d).length + balancesDeDia(d).length; const sel = ymd(d) === ymd(diaSel); const hoyCell = esHoy(d); return (
                  <button key={ymd(d)} type="button" aria-pressed={sel} onClick={() => setDiaSel(d)} style={{ minWidth: 0, border: sel ? `1px solid ${C.rosa}` : '1px solid var(--au-glass-border)', background: sel ? `${C.rosa}18` : 'var(--au-glass-fill)', color: hoyCell ? C.rosa : 'var(--au-text-primary)', borderRadius: RADIO, padding: '8px 0 6px', font: 'inherit', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: C.muted }}>{DIAS_COR[diaSemana(d) - 1]}</span>
                    <span className="au-num" style={{ fontSize: 16, fontWeight: 800 }}>{d.getDate()}</span>
                    <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 99, background: n ? C.rosa : 'transparent' }} />
                  </button>); })}
              </div>
              <div style={{ marginTop: 12, fontSize: 14, fontWeight: 800, color: 'var(--au-text-primary)', textTransform: 'capitalize' }}>{DIAS_LAR[diaSemana(diaSel) - 1]} {diaSel.getDate()} de {MESES[diaSel.getMonth()]}</div>
              {evsSel.length === 0 && balsSel.length === 0 && <div style={{ fontSize: 14, color: C.muted, marginTop: 6 }}>Sin servicios ni cobros este día.</div>}
              {evsSel.map((e, i) => <div key={i} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, e.reserva_id, e.folio)} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--au-glass-border)', fontSize: 14, cursor: 'pointer', color: 'var(--au-text-primary)' }}>{e.esPrimero && <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--au-success-fg)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Llegada</span>}<strong style={{ color: C.rosa }}>{e.folio}</strong><span>{e.cliente}</span><span style={{ color: C.muted }}>· {e.nombre}{e.pickup ? ` · pickup ${e.pickup}` : ''}</span></div>)}
              {balsSel.map((b) => <div key={'b' + b.reserva_id} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, b.reserva_id, b.folio)} style={{ padding: '8px 0', borderBottom: '1px solid var(--au-glass-border)', fontSize: 14, fontWeight: 800, color: C.yellow, cursor: 'pointer' }}>Cobras {String(b.pago_balance).toUpperCase()} · {b.folio} · {$0(b.pendiente)}</div>)}
            </div>); })()}
          {isAgent && vistaCal === 'semana' && (
            <div style={{ ...card, padding: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
                {diasSemana.map((d) => { const evs = eventosDeDia(d); const bals = balancesDeDia(d); const hoyCell = esHoy(d); return (
                  <div key={ymd(d)} style={{ minWidth: 0, borderRadius: RADIO, padding: 8, background: hoyCell ? `${C.rosa}12` : 'var(--au-glass-fill)', border: hoyCell ? `1px solid ${C.rosa}` : '1px solid var(--au-glass-border)' }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: hoyCell ? C.rosa : 'var(--au-text-primary)' }}>{DIAS_COR[diaSemana(d) - 1]} {d.getDate()}</div>
                    {evs.length === 0 && bals.length === 0 && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>—</div>}
                    {evs.map((e, i) => <div key={i} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, e.reserva_id, e.folio)} style={{ marginTop: 6, fontSize: 12, cursor: 'pointer', color: 'var(--au-text-primary)' }}>{e.esPrimero && <span style={{ fontWeight: 800, color: 'var(--au-success-fg)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Llegada </span>}<strong style={{ color: C.rosa }}>{e.folio}</strong> {e.cliente}<div style={{ color: C.muted }}>{e.nombre}{e.pickup ? ` · ${e.pickup}` : ''}</div></div>)}
                    {bals.map((b) => <div key={'b' + b.reserva_id} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, b.reserva_id, b.folio)} style={{ marginTop: 6, fontSize: 12, fontWeight: 800, color: C.yellow, cursor: 'pointer' }}>Cobras {String(b.pago_balance).toUpperCase()} · {b.folio} · {$0(b.pendiente)}</div>)}
                  </div>); })}
              </div>
            </div>
          )}
          {isAgent && (
            <div style={{ ...card, padding: 14, marginTop: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--au-text-primary)', marginBottom: 8 }}>Próximos 7 días</div>
              {proximos.length === 0 ? <div style={{ fontSize: 14, color: C.muted }}>Sin servicios ni cobros en los próximos 7 días.</div> : proximos.map((x, i) => x.balance
                ? <div key={'p' + i} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, x.balance.reserva_id, x.balance.folio)} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--au-glass-border)', fontSize: 14, cursor: 'pointer', flexWrap: 'wrap' }}><span style={{ color: C.muted, minWidth: 72 }}>{fechaDia(x.fecha)}</span><strong style={{ color: C.yellow }}>Cobras {String(x.balance.pago_balance).toUpperCase()} · {x.balance.folio} · {$0(x.balance.pendiente)}</strong></div>
                : <div key={'p' + i} role="link" tabIndex={0} onClick={(ev) => abrirFolio(ev, x.reserva_id, x.folio)} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--au-glass-border)', fontSize: 14, cursor: 'pointer', flexWrap: 'wrap', alignItems: 'center' }}><span style={{ color: C.muted, minWidth: 72 }}>{fechaDia(x.fecha)}</span>{x.esPrimero && <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--au-success-fg)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Llegada</span>}<strong style={{ color: C.rosa }}>{x.folio}</strong><span>{x.cliente}</span><span style={{ color: C.muted }}>· {x.nombre}{x.pickup ? ` · ${x.pickup}` : ''}</span></div>)}
            </div>
          )}
        </>
      )}

      {/* ════════ Popup del día ════════ */}
      {dayPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20, backdropFilter: 'blur(6px)' }} onClick={() => setDayPopup(null)}>
          <div style={{ ...card, marginBottom: 0, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 20 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--au-text-primary)', textTransform: 'capitalize' }}>
                  {DIAS_LAR[diaSemana(dayPopup) - 1]} {dayPopup.getDate()} de {MESES[dayPopup.getMonth()]}
                </div>
                {notaDe(dayPopup) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: `${C.rosa}18`, color: C.rosa, border: `1px solid ${C.rosa}55`, borderRadius: RADIO, padding: '4px 10px', fontSize: 12, fontWeight: 800 }}>
                    {notaDe(dayPopup).emoji || '📣'} {notaDe(dayPopup).nota}
                  </span>
                )}
              </div>
              <button onClick={() => setDayPopup(null)} className="au-btn au-btn--ghost" style={{ padding: '4px 10px' }}>{<Ic n="X" chico />}</button>
            </div>
            {isAgent
              ? <DiaLectura C={C} dayPopup={dayPopup} turnosDe={turnosDe} plantillaDe={plantillaDe} agentes={agentesAsignables} agName={agName} loggedAgent={loggedAgent} eventosDia={eventosDeDia(dayPopup)} balancesDia={balancesDeDia(dayPopup)} onAbrirFolio={(ev, id, folio) => { setDayPopup(null); abrirFolio(ev, id, folio); }} />
              : <DiaEdicion C={C} agentes={agentesAsignables} editState={editState} setEditState={setEditState} plantillaDe={plantillaDe} ds={diaSemana(dayPopup)} onGuardar={guardarDia} busy={busy} agName={agName}
                  notaDia={notaDe(dayPopup)} fecha={ymd(dayPopup)} onGuardarNota={guardarNota} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Vista de lectura del día (agente) ──
function DiaLectura({ C, dayPopup, turnosDe, plantillaDe, agentes, agName, loggedAgent, eventosDia = [], balancesDia = [], onAbrirFolio = null }) {
  const tds = turnosDe(dayPopup);
  const ds = ((dayPopup.getDay() + 6) % 7) + 1;
  // APP 2/19: tus servicios de este día (tocar abre el folio)
  const misServicios = (eventosDia.length || balancesDia.length) ? (
    <div style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--au-glass-border)' }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--au-text-primary)', marginBottom: 6 }}>Tus servicios</div>
      {eventosDia.map((e, i) => (
        <div key={i} role="link" tabIndex={0} onClick={(ev) => onAbrirFolio && onAbrirFolio(ev, e.reserva_id, e.folio)} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '5px 0', fontSize: 14, cursor: 'pointer', color: 'var(--au-text-primary)' }}>
          {e.esPrimero && <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--au-success-fg)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Llegada</span>}<strong style={{ color: C.rosa }}>{e.folio}</strong><span>{e.cliente}</span><span style={{ color: C.muted }}>· {e.nombre}{e.pickup ? ` · pickup ${e.pickup}` : ''}</span>
        </div>
      ))}
      {balancesDia.map((b) => (
        <div key={'b' + b.reserva_id} role="link" tabIndex={0} onClick={(ev) => onAbrirFolio && onAbrirFolio(ev, b.reserva_id, b.folio)} style={{ padding: '5px 0', fontSize: 14, cursor: 'pointer', color: C.yellow, fontWeight: 800 }}>Cobras {String(b.pago_balance).toUpperCase()} · {b.folio} · {$0(b.pendiente)}</div>
      ))}
    </div>
  ) : null;
  const bloque = (turnoKey) => {
    const lista = tds.filter((t) => t.turno === turnoKey && t.modalidad !== 'descanso');
    const T = TURNOS[turnoKey];
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--au-text-primary)', marginBottom: 6 }}>{T.icon} {T.label} <span style={{ color: C.muted, fontWeight: 500, fontSize: 12 }}>· {T.horario}</span></div>
        {lista.length === 0 ? <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Sin agentes asignados.</div> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {lista.map((t) => { const m = MOD[t.modalidad] || MOD.oficina; const col = m.c(C); const mio = loggedAgent && String(t.agente_id) === String(loggedAgent.id);
              return <span key={t.id || t.agente_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: `${col}18`, color: col, border: `1px solid ${col}${mio ? '' : '45'}`, boxShadow: mio ? `0 0 0 1.5px ${col}55` : undefined, borderRadius: RADIO_TARJETA, padding: '5px 10px', fontSize: 12, fontWeight: 700 }}>{m.icon} {agName(t.agente_id)}{t.notas ? ` · ${t.notas}` : ''}</span>; })}
          </div>
        )}
      </div>
    );
  };
  const descansos = tds.filter((t) => t.modalidad === 'descanso');
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,240px)', gap: 16 }}>
      <div>
        {misServicios}
        {bloque('manana')}
        {bloque('tarde')}
        {descansos.length > 0 && (
          <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--au-glass-border)', fontSize: 12, color: C.muted }}>
            Descansan: {descansos.map((t) => agName(t.agente_id)).join(', ')}
          </div>
        )}
      </div>
      {/* Columna lateral: horario habitual (plantilla) del día */}
      <div style={{ borderLeft: '1px solid var(--au-glass-border)', paddingLeft: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--au-text-faint)', marginBottom: 8 }}>Horario habitual</div>
        {agentes.map((a) => {
          const pl = plantillaDe(a.id, ds);
          const m = pl ? (MOD[pl.modalidad] || MOD.oficina) : null;
          const T = pl ? TURNOS[pl.turno] : null;
          return (
            <div key={a.id} style={{ fontSize: 12, color: 'var(--au-text-primary)', marginBottom: 4 }}>
              <strong>{a.nombre.split(/\s+/)[0]}</strong>{' · '}
              {pl ? <span style={{ color: m.c(C) }}>{m.icon} {T ? T.horario : ''}</span> : <span style={{ color: C.muted }}>sin plantilla</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Edición del día (admin) ──
function DiaEdicion({ C, agentes, editState, setEditState, plantillaDe, ds, onGuardar, busy, agName, notaDia, fecha, onGuardarNota }) {
  const set = (id, patch) => setEditState((s) => ({ ...s, [id]: { ...(s[id] || { turno: '', modalidad: '', notas: '' }), ...patch } }));
  const inputCss = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO, padding: '4px 6px', color: 'var(--au-text-primary)', fontSize: 12, outline: 'none' };
  const [nEmoji, setNEmoji] = useState(notaDia?.emoji || '');
  const [nNota, setNNota] = useState(notaDia?.nota || '');
  return (
    <div>
      {/* Nota del día (banner que ven todos) */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--au-glass-border)' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.rosa }}>Nota del día</span>
        <input value={nEmoji} onChange={(e) => setNEmoji(e.target.value)} placeholder="emoji" maxLength={4} style={{ ...inputCss, width: 60, textAlign: 'center', fontSize: 14 }} />
        <input value={nNota} onChange={(e) => setNNota(e.target.value)} placeholder="Ej. JUNTA, TRAINING… (vacío = borrar)" style={{ ...inputCss, flex: 1, minWidth: 140 }} />
        <button onClick={() => onGuardarNota(fecha, nEmoji, nNota)} disabled={busy} className="au-btn au-btn--secondary" style={{ height: 32, boxSizing: 'border-box', padding: '0 12px', fontSize: 12 }}>{busy ? '…' : 'Guardar nota'}</button>
      </div>
      <div style={{ maxHeight: '60vh', overflow: 'auto', marginBottom: 12 }}>
        {agentes.map((a) => {
          const v = editState[a.id] || { turno: '', modalidad: '', notas: '' };
          const pl = plantillaDe(a.id, ds);
          return (
            <div key={a.id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid var(--au-glass-border)' }}>
              <div style={{ minWidth: 120, fontSize: 12, fontWeight: 600, color: 'var(--au-text-primary)' }}>
                {a.nombre.split(/\s+/)[0]}
                {pl && <span style={{ fontSize: 12, color: C.muted, display: 'block' }}>habitual: {TURNOS[pl.turno]?.icon}{MOD[pl.modalidad]?.icon}</span>}
              </div>
              <select value={v.turno} onChange={(e) => set(a.id, { turno: e.target.value })} style={inputCss}>
                <option value="">— turno —</option>
                <option value="manana">Mañana</option>
                <option value="tarde">Tarde</option>
                <option value="dia">Día</option>
              </select>
              <select value={v.modalidad} onChange={(e) => set(a.id, { modalidad: e.target.value })} style={inputCss}>
                <option value="">— modalidad —</option>
                {Object.entries(MOD).map(([k, m]) => <option key={k} value={k}>{m.icon} {m.label}</option>)}
              </select>
              <input value={v.notas} onChange={(e) => set(a.id, { notas: e.target.value })} placeholder="notas" style={{ ...inputCss, flex: 1, minWidth: 90 }} />
              {(v.turno || v.modalidad) && <button onClick={() => setEditState((s) => { const n = { ...s }; delete n[a.id]; return n; })} title="Quitar asignación" style={{ ...inputCss, cursor: 'pointer', color: C.rosa, border: `1px solid ${C.rosa}40` }}>{<Ic n="X" chico />}</button>}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Guarda un turno + modalidad por agente. Cambios sobrescriben lo del día para los agentes listados.</div>
      <button onClick={onGuardar} disabled={busy} className="au-btn au-btn--primary" style={{ width: '100%' }}>{busy ? 'Guardando…' : 'Guardar día'}</button>
    </div>
  );
}

// ── Editor de plantillas semanales (admin) ──
function PlantillasEditor({ C, card, agentes, plantillas, recargar, loggedAgent }) {
  const [agId, setAgId] = useState(agentes[0]?.id || '');
  const [rows, setRows] = useState({}); // { dia_semana: {turno, modalidad} }
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const st = {};
    plantillas.filter((p) => String(p.agente_id) === String(agId)).forEach((p) => { st[p.dia_semana] = { turno: p.turno, modalidad: p.modalidad }; });
    setRows(st);
  }, [agId, plantillas]);
  const inputCss = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO, padding: '5px 8px', color: 'var(--au-text-primary)', fontSize: 12, outline: 'none' };
  const set = (dia, patch) => setRows((s) => ({ ...s, [dia]: { ...(s[dia] || { turno: '', modalidad: '' }), ...patch } }));
  const guardar = async () => {
    if (!agId) return;
    setBusy(true);
    try {
      // Borra la plantilla del agente y reinserta lo capturado (upsert onConflict agente_id,dia_semana).
      await supabase.from('horario_plantilla').delete().eq('agente_id', agId);
      const ins = Object.entries(rows).filter(([, v]) => v && v.turno && v.modalidad)
        .map(([dia_semana, v]) => ({ agente_id: Number(agId), dia_semana: Number(dia_semana), turno: v.turno, modalidad: v.modalidad }));
      if (ins.length) { const { error } = await supabase.from('horario_plantilla').upsert(ins, { onConflict: 'agente_id,dia_semana' }); if (error) throw error; }
      await recargar();
      alert('Plantilla guardada.');
    } catch (e) { alert('No se pudo guardar la plantilla: ' + (e.message || e)); }
    setBusy(false);
  };
  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--au-text-primary)' }}>Horario habitual semanal</div>
        <select value={agId} onChange={(e) => setAgId(e.target.value)} style={{ ...inputCss, marginLeft: 'auto' }}>
          {agentes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
      </div>
      {[1, 2, 3, 4, 5, 6, 7].map((dia) => {
        const v = rows[dia] || { turno: '', modalidad: '' };
        return (
          <div key={dia} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--au-glass-border)' }}>
            <div style={{ minWidth: 90, fontSize: 12, fontWeight: 600, color: dia >= 6 ? C.naranja : 'var(--au-text-primary)' }}>{DIAS_LAR[dia - 1]}</div>
            <select value={v.turno} onChange={(e) => set(dia, { turno: e.target.value })} style={inputCss}>
              <option value="">— turno —</option>
              <option value="manana">Mañana</option>
              <option value="tarde">Tarde</option>
              <option value="dia">Día</option>
            </select>
            <select value={v.modalidad} onChange={(e) => set(dia, { modalidad: e.target.value })} style={inputCss}>
              <option value="">— modalidad —</option>
              {Object.entries(MOD).map(([k, m]) => <option key={k} value={k}>{m.icon} {m.label}</option>)}
            </select>
            {(v.turno || v.modalidad) && <button onClick={() => setRows((s) => { const n = { ...s }; delete n[dia]; return n; })} style={{ ...inputCss, cursor: 'pointer', color: C.rosa }}>{<Ic n="X" chico />}</button>}
          </div>
        );
      })}
      <button onClick={guardar} disabled={busy} className="au-btn au-btn--primary" style={{ marginTop: 12 }}>{busy ? 'Guardando…' : 'Guardar plantilla'}</button>
    </div>
  );
}

// ── CRUD mínimo de IPs de oficina (admin) — solo lista, sin lógica de asistencia ──
function IpsEditor({ C, card, ips, recargar }) {
  const [etiqueta, setEtiqueta] = useState('');
  const [ip, setIp] = useState('');
  const [busy, setBusy] = useState(false);
  const inputCss = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO, padding: '9px 12px', color: 'var(--au-text-primary)', fontSize: 14, outline: 'none' };
  const agregar = async () => {
    if (!etiqueta.trim() || !ip.trim()) { alert('Etiqueta e IP son obligatorias.'); return; }
    setBusy(true);
    try {
      const { error } = await supabase.from('oficina_ips').insert({ etiqueta: etiqueta.trim(), ip: ip.trim(), activa: true });
      if (error) throw error;
      setEtiqueta(''); setIp(''); await recargar();
    } catch (e) { alert('No se pudo agregar la IP: ' + (e.message || e)); }
    setBusy(false);
  };
  const toggle = async (row) => {
    try { const { error } = await supabase.from('oficina_ips').update({ activa: !row.activa }).eq('id', row.id); if (error) throw error; await recargar(); }
    catch (e) { alert('No se pudo actualizar: ' + (e.message || e)); }
  };
  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--au-text-primary)', marginBottom: 4 }}>IPs de oficina</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Solo administración de la lista. El check-in/asistencia es una fase futura.</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)} placeholder="Etiqueta (ej. Oficina Cancún)" style={{ ...inputCss, flex: 1, minWidth: 160 }} />
        <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="IP (ej. 189.203.x.x)" style={{ ...inputCss, flex: 1, minWidth: 140 }} />
        <button onClick={agregar} disabled={busy} className="au-btn au-btn--primary">{busy ? '…' : <><Ic n="PlusCircle" chico /> Agregar</>}</button>
      </div>
      {ips.length === 0 ? <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>Sin IPs registradas.</div> : ips.map((row) => (
        <div key={row.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--au-glass-border)' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--au-text-primary)', minWidth: 160 }}>{row.etiqueta}</span>
          <span className="au-num" style={{ fontSize: 12, color: C.muted, flex: 1 }}>{row.ip}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: row.activa ? C.green : C.muted }}>{row.activa ? '● activa' : '○ inactiva'}</span>
          <button onClick={() => toggle(row)} className="au-btn au-btn--ghost" style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>{row.activa ? 'Desactivar' : 'Activar'}</button>
        </div>
      ))}
    </div>
  );
}
